/**
 * FontPicker — Dropdown font selector with search + lazy Google Fonts loading.
 */
import { useState, useEffect, useRef } from 'react';
import { FONT_LIST, loadFontBatch } from '../utils/brandUtils.js';

export default function FontPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  // Load first batch when dropdown opens
  useEffect(() => {
    if (open) {
      const filtered = search ? FONT_LIST.filter(f => f.toLowerCase().includes(search.toLowerCase())) : FONT_LIST;
      loadFontBatch(filtered.slice(0, 20));
    }
  }, [open, search]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const filtered = search ? FONT_LIST.filter(f => f.toLowerCase().includes(search.toLowerCase())) : FONT_LIST;

  return (
    <div ref={ref} className="relative w-full">
      <button onClick={() => setOpen(!open)} className="w-full text-left text-[11px] px-2 py-1.5 border border-gray-300 rounded-md bg-white hover:border-blue-400 flex items-center justify-between">
        <span style={value ? { fontFamily: `'${value}', sans-serif` } : {}}>{value || 'Pick a font...'}</span>
        <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-1.5 border-b border-gray-100">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search fonts..." autoFocus
              className="w-full text-[11px] px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-400" />
          </div>
          <div className="overflow-y-auto max-h-48" onScroll={e => {
            const el = e.target;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
              loadFontBatch(filtered.slice(0, Math.min(filtered.length, 50)));
            }
          }}>
            {filtered.map(f => (
              <button key={f} onClick={() => { onChange(f); setOpen(false); setSearch(''); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors ${value === f ? 'bg-blue-100 text-blue-700' : 'text-gray-700'}`}
                style={{ fontFamily: `'${f}', sans-serif` }}>
                {f}
              </button>
            ))}
            {filtered.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No fonts match</div>}
          </div>
        </div>
      )}
    </div>
  );
}
